/**
 * src/lib/imageCache.js
 * Non-invasive in-memory cache for public image URLs to prevent redundant fetches.
 * Tracks loaded public URLs (e.g., /storage/v1/object/public/...) to avoid re-fetching
 * the same image during re-renders or component remounts.
 */

const loadedImages = new Map(); // url -> true

/**
 * Check if a public image URL has been loaded (cached).
 * @param {string} url - Public image URL
 * @returns {boolean} - True if this URL was previously loaded
 */
export function isImageCached(url) {
  if (!url || typeof url !== "string") return false;
  return loadedImages.has(url);
}

/**
 * Mark a public image URL as loaded.
 * Call this after image.onload or after a successful fetch.
 * @param {string} url - Public image URL
 */
export function markImageAsLoaded(url) {
  if (!url || typeof url !== "string") return;
  loadedImages.set(url, true);
}

/**
 * Get cache stats (dev debugging).
 * @returns {object} - { size: number, urls: string[] }
 */
export function getImageCacheStats() {
  return {
    size: loadedImages.size,
    urls: Array.from(loadedImages.keys()),
  };
}

/**
 * Clear the entire cache (rarely needed).
 */
export function clearImageCache() {
  loadedImages.clear();
}

/**
 * Hook to auto-mark an image as loaded when onLoad fires.
 * Use inside a React component with an <img /> element.
 * @param {string} url - Image URL
 * @returns {object} - { onLoad, onError } handlers
 *
 * Example:
 *   const { onLoad, onError } = useImageCaching(logoUrl);
 *   <img src={logoUrl} onLoad={onLoad} onError={onError} />
 */
export function createImageHandlers(url) {
  return {
    onLoad: () => markImageAsLoaded(url),
    onError: () => {
      // Silently handle error; don't cache failed loads
    },
  };
}
